import styled, { css } from 'styled-components';
import type { EstimateTone } from './helpers/strategySuggestionFormat';

/*
 * One section of the suggestion. Its own card rather than the shared Box,
 * whose 70px min-height would pad out the collapsed "No change" rows.
 * Unchanged sections are the same white card, only slimmer and folded.
 */
export const SectionCard = styled.div<{ $muted?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: ${({ $muted }) => ($muted ? '12px 16px' : '16px')};
  border-radius: 20px;
  background: ${({ theme }) => theme.white};
  box-shadow: ${({ theme }) => theme.shadows.shadow2};
`;

/** The whole head of a section is the click target: the label ticks it. */
export const SectionHead = styled.label<{ $clickable: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

/** "No change" rows open on a click anywhere along them. */
export const MutedToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: inherit;
  text-align: left;
`;

/*
 * Now and suggested side by side, the way the cards on the screen would read
 * before and after; stacked on a phone, where two columns of rule lines
 * would wrap every few words.
 */
export const ValueCompare = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;

  @media (max-width: 800px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }
`;

export const ValueColumn = styled.div<{ $suggested?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${({ $suggested, theme }) =>
    $suggested ? theme.transparentGreen : theme.gray};
  overflow-wrap: anywhere;
`;

/* The four baseline numbers: a row on desktop, two by two on a phone. */
export const FigureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

/*
 * White with the section cards' shadow: grey tiles vanished into the modal's
 * beige, and flat white ones still sat loose on it.
 */
export const Figure = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${({ theme }) => theme.white};
  box-shadow: ${({ theme }) => theme.shadows.shadow2};
`;

/*
 * What the data was ("Little data", "+ 2 nearby locations"). The shared Pill
 * only offers shadow1, whose 30px blur swamps a chip this small; this one
 * wears the same shadow as the cards around it.
 */
export const DataPill = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 2px 12px;
  border-radius: 20px;
  background: ${({ theme }) => theme.white};
  box-shadow: ${({ theme }) => theme.shadows.shadow2};
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  color: ${({ theme }) => theme.darkGray};
`;

export const FigureValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme }) => theme.darkGray};
`;

const toneColors = {
  good: { color: 'green', background: 'transparentGreen' },
  bad: { color: 'primary', background: 'transparentRed' },
  neutral: { color: 'darkGray', background: 'gray' },
} as const;

/** An AI estimate: green when it helps, red when it costs. */
export const EstimateBadge = styled.span<{ $tone: EstimateTone }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  color: ${({ $tone, theme }) => theme[toneColors[$tone].color]};
  background: ${({ $tone, theme }) => theme[toneColors[$tone].background]};
`;

export const BulletList = styled.ul`
  margin: 0;
  padding-left: 18px;

  li {
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme.darkGray};

    & + li {
      margin-top: 6px;
    }
  }
`;

/** The bare button around "Show more", centred under the text it opens. */
export const ReadMoreToggle = styled.button`
  align-self: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
`;

/** Clamps the reasoning to three lines until it is opened. */
export const Clamped = styled.div<{ $open: boolean }>`
  ${({ $open }) =>
    !$open &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
`;
